import { Component, Input, forwardRef, CUSTOM_ELEMENTS_SCHEMA, HostListener, signal, ElementRef, inject, Output, EventEmitter, OnInit, OnDestroy, Renderer2 } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, FormsModule } from '@angular/forms';

export interface SelectOption {
    label: string | number;
    value: any;
}

@Component({
    selector: 'app-select',
    standalone: true,
    imports: [CommonModule, FormsModule],
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => AppSelect),
            multi: true
        }
    ],
    templateUrl: './app-select.html',
    styles: [`
    :host { display: block; }
    .custom-scrollbar::-webkit-scrollbar { width: 8px; }
    .custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
    .custom-scrollbar::-webkit-scrollbar-thumb { 
        background: color-mix(in srgb, var(--foreground) 10%, transparent); 
        border-radius: 9999px; 
    }
  `]
})
export class AppSelect implements ControlValueAccessor, OnInit, OnDestroy {
    private el = inject(ElementRef);
    private renderer = inject(Renderer2);
    private clickListener?: () => void;

    @Input() label: string = '';
    @Input() placeholder: string = '';
    @Input() options: SelectOption[] = [];
    @Input() error: string = '';
    @Input() icon: string = '';
    @Input() required: boolean = false;
    @Input() size: 'xs' | 'sm' | 'md' | 'lg' = 'md';
    @Input() variant: 'default' | 'minimal' = 'default';
    @Input() helperText: string = '';
    @Input() fullWidth: boolean = true;
    @Input() insideDialog: boolean = false;
    @Input() visualType: 'none' | 'icon' | 'color' = 'none';
    @Input() visualColor: string = '';
    @Output() valueChange = new EventEmitter<any>();

    value: any = '';
    disabled = false;
    isOpen = signal(false);
    direction = signal<'up' | 'down'>('down');

    onChange: any = () => { };
    onTouched: any = () => { };

    ngOnInit() {
        this.clickListener = this.renderer.listen('document', 'click', (event: MouseEvent) => {
            if (this.isOpen() && !this.el.nativeElement.contains(event.target)) {
                this.isOpen.set(false);
            }
        }, { capture: true });
    }

    ngOnDestroy() {
        if (this.clickListener) this.clickListener();
    }

    get selectedLabel(): string | number {
        const option = this.options.find(opt => opt.value === this.value);
        return option ? option.label : '';
    }

    writeValue(value: any): void {
        this.value = value;
    }

    registerOnChange(fn: any): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: any): void {
        this.onTouched = fn;
    }

    setDisabledState(isDisabled: boolean): void {
        this.disabled = isDisabled;
    }

    dropdownStyle = signal<any>({});
    
    toggleDropdown() {
        if (this.disabled) return;

        if (!this.isOpen() && this.insideDialog) {
            const rect = this.el.nativeElement.getBoundingClientRect();
            const spaceBelow = window.innerHeight - rect.bottom;
            const dir = spaceBelow < 250 ? 'up' : 'down';
            this.direction.set(dir);

            this.dropdownStyle.set({
                position: 'fixed',
                top: dir === 'down' ? `${rect.bottom + 4}px` : 'auto',
                bottom: dir === 'up' ? `${window.innerHeight - rect.top + 4}px` : 'auto',
                left: `${rect.left}px`,
                width: `${rect.width}px`,
                zIndex: '999999'
            });
        } else {
            this.dropdownStyle.set({});
        }

        this.isOpen.set(!this.isOpen());
    }

    @HostListener('window:scroll', ['$event'])
    @HostListener('window:resize', ['$event'])
    closeDropdown(event?: any) {
        if (this.isOpen() && this.insideDialog) {
            this.isOpen.set(false);
        }
    }

    selectOption(val: any) {
        this.value = val;
        this.isOpen.set(false);
        this.onChange(val);
        this.onTouched();
        this.valueChange.emit(val);
    }

    get sizeClasses(): string {
        const map: Record<string, string> = {
            xs: 'px-2 py-1 text-[10px] h-7',
            sm: 'px-3 py-1.5 text-xs h-9',
            md: 'px-4 py-2.5 text-sm h-11',
            lg: 'px-5 py-4 text-base h-14',
        };
        return map[this.size] ?? map['md'];
    }

    get optionSizeClasses(): string {
        const map: Record<string, string> = {
            xs: 'px-3 py-2 text-[10px]',
            sm: 'px-3.5 py-2 text-xs',
            md: 'px-4 py-2.5 text-sm',
            lg: 'px-5 py-3.5 text-base',
        };
        return map[this.size] ?? map['md'];
    }

    get variantClasses(): string {
        if (this.variant === 'minimal') {
            return '!bg-transparent !border-transparent !shadow-none !px-1 hover:!bg-foreground/[0.03] !rounded-lg !h-auto !py-0.5';
        }
        return '';
    }
}
